// Grant rules per package type (api.md 2.4, ADR-0033 section 3, ADR-0038 section 3, QUESTIONS.md Q24).
import type { Action, Ecosystem } from "../api/types";

/** The package types in the grant form, in form order. */
export const ECOSYSTEMS: Ecosystem[] = ["cargo", "oci", "go", "any"];

export const ECOSYSTEM_LABEL: Record<Ecosystem, string> = {
  cargo: "Crates",
  oci: "Containers",
  go: "Go modules",
  any: "Any type",
};

/** Help text under the package type select. It tells the glob rule of each type. */
export const ECOSYSTEM_HELP: Record<Ecosystem, string> = {
  cargo:
    'Crate names: "*" for all crates, "core-*" for a prefix, or one exact name. Case and "-" or "_" do not change the match.',
  oci: 'The repository part of the image name: "*", "apps/*" or "api". Lowercase only. "-" and "_" are different characters.',
  go: 'Glob on the module path, for example "go.acme.com/*". Lowercase. "*" only at the end, and it matches across "/". "go.acme.com/core" does not match "go.acme.com/core/v2": write "go.acme.com/core*".',
  any: 'Crates, containers and Go modules with one glob: "*", "core-*" or "apps/*". Lowercase only, and no "_".',
};

/** The glob placeholder for each type. */
export const GLOB_PLACEHOLDER: Record<Ecosystem, string> = {
  cargo: "core-*",
  oci: "apps/*",
  go: "go.acme.com/*",
  any: "*",
};

/** All actions, in display order. */
export const ALL_ACTIONS: Action[] = ["read", "publish", "yank", "delete"];

/**
 * `yank` is for crates only. `delete` is for containers and Go modules (Go uses `retract` in go.mod, not yank). A
 * grant of type `any` can hold both.
 */
export function actionsFor(ecosystem: Ecosystem): Action[] {
  if (ecosystem === "cargo") return ["read", "publish", "yank"];
  if (ecosystem === "oci" || ecosystem === "go") return ["read", "publish", "delete"];
  return ALL_ACTIONS;
}

/** Keeps only the actions the type allows. Keeps `read` if nothing is left. */
export function keepAllowed(actions: Action[], ecosystem: Ecosystem): Action[] {
  const allowed = actionsFor(ecosystem);
  const kept = actions.filter((a) => allowed.includes(a));
  return kept.length > 0 ? kept : ["read"];
}

/** The type of a grant row. A grant from before registry 0.17.0 has no field and is `cargo`. */
export function grantEcosystem(g: { ecosystem?: Ecosystem }): Ecosystem {
  return g.ecosystem ?? "cargo";
}
